import { z } from 'zod'
import { agreeTermsSchema, type AgreeTerms } from '@/models/api/agree-terms'
import type { ChangeLatchKeyRequest } from '@/models/api/change-latch-key-request'
import type { CheckCorporateAccountEmailRequest } from '@/models/api/check-corporate-account-email-request'
import type { CorporateAccountUpdateRequest } from '@/models/api/corporate-account-update-request'
import type { CreateCorporateSubAccountRequest } from '@/models/api/create-corporate-sub-account-request'
import type { GetCorporateAccountInformationRequest } from '@/models/api/get-corporate-account-information-request'
import type { ResendNotificationLetterRequest } from '@/models/api/resend-notification-letter-request'
import type { ResetLatchKeyRequest } from '@/models/api/reset-latch-key-request'
import type { SendCaptchaCodeByEmailRequest } from '@/models/api/send-captcha-code-by-email-request'
import {
  sendCaptchaCodeByEmailResponseSchema,
  type SendCaptchaCodeByEmailResponse
} from '@/models/api/send-captcha-code-by-email-response'
import {
  corporateAccountCreateResponseSchema,
  corporateAccountInformationSchema,
  corporateAccountSchema,
  type CorporateAccount,
  type CorporateAccountCreateResponse,
  type CorporateAccountInformation,
  type UpdatePermissionRequest
} from '~library/models'
import { apiValidator } from '~library/services'
import type {
  ApiFactory,
  ApiRequestConfig,
  ApiResponse
} from '~library/services'

export default class CorporateAccountApi {
  constructor(private api: ApiFactory) {}

  /**
   * API 取得企業帳號資料
   * https://hackmd.io/muyotZfIRUiYjYI9riO0rQ
   */
  @apiValidator(corporateAccountSchema)
  getData(account: string): Promise<ApiResponse<CorporateAccount>> {
    return this.api.getData(`CorporateAccount/CorporateAccount/${account}`)
  }

  /**
   * API 編輯企業帳號
   * https://hackmd.io/2p_HVEhSTMyu2sZySHQK_w
   */
  @apiValidator(z.null())
  updateData(data: CorporateAccountUpdateRequest): Promise<ApiResponse<null>> {
    return this.api.updateData('CorporateAccount/CorporateAccount', data)
  }

  /**
   * API 刪除企業帳號
   * https://hackmd.io/ANocp9AOTGCj8-AdGj0XrQ
   */
  @apiValidator(z.null())
  deleteData(account: string): Promise<ApiResponse<null>> {
    return this.api.delete(`CorporateAccount/CorporateAccount/${account}`)
  }

  /**
   * API 新增企業子帳號
   * https://hackmd.io/XiKORz6ASAyHGWlzqfIH-Q
   */
  @apiValidator(corporateAccountCreateResponseSchema)
  createCorporateSubAccount(
    data: CreateCorporateSubAccountRequest
  ): Promise<ApiResponse<CorporateAccountCreateResponse>> {
    return this.api.post('CorporateAccount/CorporateSubAccount', data)
  }

  /**
   * API 取得企業帳號同意條款資訊
   * https://hackmd.io/d8Xi1IKnRmqHPSjk1sacTg
   */
  @apiValidator(agreeTermsSchema)
  getAgreeTerms(): Promise<ApiResponse<AgreeTerms>> {
    return this.api.get('CorporateAccount/GetAgreeTerms')
  }

  /**
   * API 企業帳號同意條款
   * https://hackmd.io/K_M3HYcARXaHBKm0uDhk-Q
   */
  @apiValidator(z.null())
  updateAgreeTerms(): Promise<ApiResponse<null>> {
    return this.api.put('CorporateAccount/AgreeTerms', {})
  }

  /**
   * API 企業帳號修改密碼
   * https://hackmd.io/_0Rr9d-AQhqxwy4LBeMGhw
   */
  @apiValidator(z.null())
  changeLatchKey(data: ChangeLatchKeyRequest): Promise<ApiResponse<null>> {
    return this.api.put('CorporateAccount/ChangeLatchKey', data, {
      headers: {
        ignoreRtnCodeError: ['51', '97']
      }
    })
  }

  /**
   * API 企業帳號重設/忘記密碼
   * https://hackmd.io/VxVw_CpfRvORBOpoZsZiYw
   */
  @apiValidator(z.null())
  resetLatchKey(data: ResetLatchKeyRequest): Promise<ApiResponse<null>> {
    return this.api.post('CorporateAccount/ResetLatchKey', data, {
      headers: {
        ignoreRtnCodeError: ['51', '52']
      }
    })
  }

  /**
   * API 企業帳號忘記密碼 寄送/重寄 驗證碼通知信
   * https://hackmd.io/FG_egMQBRBiha52xbJqs_w
   */
  @apiValidator(sendCaptchaCodeByEmailResponseSchema)
  sendCaptchaCodeByEmail(
    data: SendCaptchaCodeByEmailRequest
  ): Promise<ApiResponse<SendCaptchaCodeByEmailResponse>> {
    return this.api.post('CorporateAccount/SendCaptchaCodeByEmail', data)
  }

  /**
   * API 企業帳號權限開啟/關閉
   * https://hackmd.io/NGrmlGfiQqaU-og6Bum0ww
   */
  @apiValidator(z.null())
  updatePermission(data: UpdatePermissionRequest): Promise<ApiResponse<null>> {
    return this.api.put('CorporateAccount/UpdatePermission', data)
  }

  /** API 重寄企業帳號通知信
   * https://hackmd.io/4R_RsONfQgKy3LXD4PMlcA
   */
  @apiValidator(z.null())
  resendNotificationLetter(
    data: ResendNotificationLetterRequest
  ): Promise<ApiResponse<null>> {
    return this.api.post('CorporateAccount/ResendNotificationLetter', data)
  }

  /**
   * API 檢核企業帳號綁定信箱
   * https://hackmd.io/NzRNj51tQ8uhbMS1G5tO8w
   */
  @apiValidator(z.null())
  checkEmail(
    data: CheckCorporateAccountEmailRequest,
    config?: ApiRequestConfig
  ): Promise<ApiResponse<null>> {
    return this.api.post('CorporateAccount/CheckEmail', data, {
      headers: {
        ignoreRtnCodeError: ['45']
      },
      ...config
    })
  }

  /**
   * API 查詢企業帳號資料
   * https://hackmd.io/2BhJT-6QT5uukpD5_BE5uQ
   */
  @apiValidator(corporateAccountInformationSchema.array())
  getCorporateAccountInformation(
    data: GetCorporateAccountInformationRequest
  ): Promise<ApiResponse<CorporateAccountInformation[]>> {
    return this.api.post(
      'CorporateAccount/GetCorporateAccountInformation',
      data
    )
  }
}
